import { config } from "@/shared/lib/config";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { Button, Field, FormCheckbox, FormInput, RHF } from "@devhop/ui";
import { Plus, Trash2 } from "lucide-react";
import type { Control, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import type { ChecklistFormValues } from "./ChecklistForm";

type FormLookupItem = { id: string; name: string };

type ChecklistItemsFieldProps = {
  control: Control<ChecklistFormValues>;
  watch: UseFormWatch<ChecklistFormValues>;
  setValue: UseFormSetValue<ChecklistFormValues>;
};

export function ChecklistItemsField({
  control,
  watch,
  setValue,
}: ChecklistItemsFieldProps) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "items",
  });

  const addItem = () => {
    append({
      title: "",
      description: null,
      order: fields.length,
      isRequired: true,
      allowNotes: false,
      formId: null,
    });
  };

  const formLookupQueryFn = async ({
    search,
    pageParam,
  }: {
    search: string;
    pageParam: number;
  }) => {
    const limit = 20;
    const skip = (pageParam - 1) * limit;
    const params = new URLSearchParams({
      limit: String(limit),
      skip: String(skip),
    });
    if (search?.trim()) params.set("q", search.trim());
    const res = await fetch(`${config.apiUrl}/forms/lookup?${params}`, {
      credentials: "include",
    });
    if (!res.ok) return { items: [], nextPage: null };
    const data = await res.json();
    const items = (data.items ?? []) as FormLookupItem[];
    const total = data.total ?? 0;
    const nextPage = skip + items.length < total ? pageParam + 1 : null;
    return { items, nextPage };
  };
  const formLookupPreloadFn = async (id: string) => {
    const res = await fetch(`${config.apiUrl}/forms/lookup/${id}`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const data = await res.json();
    const item = data?.item as FormLookupItem | undefined;
    return item ?? null;
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    move(fromIndex, toIndex);
    // Update order values after move
    const currentItems = watch("items");
    currentItems.forEach((_, index) => {
      setValue(`items.${index}.order`, index);
    });
  };

  return (
    <Field name="items" label="ລາຍການງານ">
      <div className="space-y-3">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="flex gap-2 rounded-lg border bg-card p-3"
          >
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-muted-foreground text-sm">
                  #{index + 1}
                </span>
              </div>
              <FormInput
                name={`items.${index}.title`}
                placeholder="ຕົວຢ່າງ: ເປີດລະບົບຂາຍ"
                requiredMark
                className="flex-1"
              />
              <FormInput
                name={`items.${index}.description`}
                placeholder="ຄໍາອະທິບາຍ (ບໍ່ບັງຄັບ)"
                className="flex-1"
              />
              <RHF.Controller
                control={control}
                name={`items.${index}.formId`}
                render={({ field }) => (
                  <InfiniteCombobox<FormLookupItem>
                    queryKey={["forms", "lookup", "checklist", String(index)]}
                    queryFn={formLookupQueryFn}
                    preloadQueryFn={formLookupPreloadFn}
                    getLabel={(item) => item.name}
                    getValue={(item) => item.id}
                    value={field.value ?? ""}
                    onValueChange={(v) => field.onChange(v || null)}
                    placeholder="ເລືອກຟອມຈາກ Form Center (ບໍ່ບັງຄັບ)"
                    clearable
                    className="flex-1"
                  />
                )}
              />
              <div className="flex gap-4">
                <FormCheckbox
                  name={`items.${index}.isRequired`}
                  label="ຈໍາເປັນຕ້ອງເຮັດ"
                />
                <FormCheckbox
                  name={`items.${index}.allowNotes`}
                  label="ອະນຸຍາດໃຫ້ເພີ່ມໝາຍເຫດ"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {index > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveItem(index, index - 1)}
                  title="ຍ້າຍຂຶ້ນ"
                >
                  ↑
                </Button>
              )}
              {index < fields.length - 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveItem(index, index + 1)}
                  title="ຍ້າຍລົງ"
                >
                  ↓
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(index)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          onClick={addItem}
          className="w-full"
        >
          <Plus className="h-4 w-4" />
          ເພີ່ມລາຍການ
        </Button>

        {fields.length === 0 && (
          <p className="text-center text-muted-foreground text-sm">
            ຍັງບໍ່ມີລາຍການງານ. ກົດປຸ່ມ "ເພີ່ມລາຍການ" ເພື່ອເລີ່ມຕົ້ນ
          </p>
        )}
      </div>
    </Field>
  );
}
